import { Box, Checkbox, Chip, Stack, Typography } from '@mui/joy';
import { Circle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { AttributeVersionType } from 'companySettings/enums';
import { IPartFinderVersion } from 'companySettings/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { formatRelativeDate } from 'core/utils';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

// index.css sizes every svg at 1em, so the dots take their size from fontSize (not `size`), and
// they must not shrink: in a flex row a long line would squeeze them.
const DOT_STYLE = { fontSize: 6, flexShrink: 0 };

interface IProps<TChanges> {
  version: IPartFinderVersion<TChanges>;
  formatChanges: (changes: TChanges, isPublish: boolean) => string;
  isSelected: boolean;
  onToggle: () => void;
  isSelectable?: boolean;
  isLast?: boolean;
}

const getBadgeColor = (versionType: AttributeVersionType) => {
  if (AttributeVersionType.DRAFT === versionType) {
    return Color.WARNING;
  }

  return AttributeVersionType.RELEASE === versionType ? Color.SUCCESS : Color.NEUTRAL;
};

// One version: its summary, who published it and when, and the saves behind it. The draft a record
// is being edited in shows what publishing it would record; it cannot be compared yet. The version
// badges sit on a timeline that runs down to the next, older version.
export const VersionEntry = <TChanges,>({
  version,
  formatChanges,
  isSelected,
  onToggle,
  isSelectable = true,
  isLast = false,
}: IProps<TChanges>) => {
  const { t } = useTranslation();

  const isDraft = AttributeVersionType.DRAFT === version.type;
  // The saves come oldest first; the timeline reads newest first, like the versions above them.
  const changes = [...(version.changes ?? [])].reverse();

  return (
    <Stack direction={Direction.ROW} sx={{ gap: 1.5 }}>
      {/* A draft cannot be compared, so its box is hidden; it keeps its space so the badges and
          the timeline stay in line. */}
      <Checkbox
        checked={isSelected}
        disabled={isDraft || (!isSelected && !isSelectable)}
        onChange={onToggle}
        sx={{ alignSelf: 'flex-start', mt: 0.25, ...(isDraft && { visibility: 'hidden' }) }}
      />

      <Stack sx={{ alignItems: 'center' }}>
        <Chip
          size={Size.SM}
          variant={Variant.SOFT}
          color={getBadgeColor(version.type)}
          startDecorator={<Circle weight="fill" style={DOT_STYLE} />}
        >
          {isDraft
            ? t('labelAttributeStatusDraft')
            : t('labelVersionNumber', { number: version.number })}
        </Chip>
        {!isLast && <Box sx={{ flex: 1, width: '1px', my: 0.5, backgroundColor: 'neutral.300' }} />}
      </Stack>

      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.5, pb: isLast ? 0 : 2.5 }}>
        <Typography level={Level.TITLE_SM}>{formatChanges(version.summary, !isDraft)}</Typography>
        <Typography level={Level.BODY_SM} sx={{ color: 'neutral.600' }}>
          {isDraft
            ? t('labelNotPublishedYet')
            : t('labelAuthorAndTime', {
                author: version.publishedByName ?? DEFAULT_PLACEHOLDER,
                time: formatRelativeDate(version.versionedAt),
              })}
        </Typography>

        {changes.map((change) => (
          <Box
            key={change.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'center',
              columnGap: 1,
              rowGap: 0.25,
              mt: 1,
            }}
          >
            <Circle weight="fill" style={DOT_STYLE} />
            <Typography level={Level.BODY_SM}>
              {formatChanges(change.details, change.published)}
            </Typography>
            <Typography level={Level.BODY_SM} sx={{ gridColumn: 2, color: 'neutral.600' }}>
              {t('labelAuthorAndTime', {
                author: change.userName ?? DEFAULT_PLACEHOLDER,
                time: formatRelativeDate(change.createdAt),
              })}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};
